import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import GenericImportModal from '../../components/common/GenericImportModal';
import Pagination from '../../components/common/Pagination';
import PermissionGuard from '../../components/common/PermissionGuard';
import * as XLSX from 'xlsx';
import SortableHeader from '../../components/common/SortableHeader';
import { QRCodeSVG } from 'qrcode.react';
import { useSocket } from '../../context/SocketContext';

export const resolveImageUrl = (src) => {
    if (!src) return src;
    const getBaseUrl = () => {
        if (window.location.port === '3000') {
            return `${window.location.protocol}//${window.location.hostname}:3001`;
        }
        return '/api'; // Préfixe nécessaire pour que Nginx proxy vers le backend
    };
    return (src.startsWith('/') && !src.startsWith('//'))
        ? `${getBaseUrl()}${src}`
        : src;
};

const ProductImage = ({ src, alt, name, className = "w-full h-full object-cover" }) => {
    const [error, setError] = useState(false);

    // Reset error state when src changes
    useEffect(() => {
        setError(false);
    }, [src]);

    const finalSrc = resolveImageUrl(src);

    const handleError = () => {
        console.error(`[ProductImage] Erreur de chargement pour : ${finalSrc}`);
        setError(true);
    };

    if (!src || error) {
        return (
            <div className={`${className} bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400`}>
                <span className="material-symbols-outlined text-[20px]">image_not_supported</span>
            </div>
        );
    }

    return (
        <img
            src={finalSrc}
            alt={alt || name}
            className={className}
            onError={handleError}
        />
    );
};

const QuickEditInput = ({ initialValue, type = "text", onSave, className }) => {
    const [val, setVal] = useState(initialValue);

    // Update local state if external data changes (e.g. via websocket)
    useEffect(() => {
        setVal(initialValue);
    }, [initialValue]);

    return (
        <input
            type={type}
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onBlur={() => {
                if (String(val) !== String(initialValue)) {
                    onSave(val);
                }
            }}
            className={className}
        />
    );
};

const Inventory = () => {
    const { user, hasPermission } = useAuth();
    const { showToast, showConfirm } = useUI();
    const { socket } = useSocket();
    const [products, setProducts] = useState([]);
    const [stats, setStats] = useState(null);
    const [categories, setCategories] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);
    const [limit, setLimit] = useState(parseInt(localStorage.getItem('app_items_per_page')) || 25);

    // Filters & Search
    const [search, setSearch] = useState('');
    const [filterCategory, setFilterCategory] = useState('Tous');
    const [filterStatus, setFilterStatus] = useState('Tous');
    const [filterWarehouse, setFilterWarehouse] = useState('Tous');
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('ASC');

    // Refs for performance stability
    const categoriesRef = React.useRef([]);
    const warehousesRef = React.useRef([]);


    // Modal
    const [showModal, setShowModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [formData, setFormData] = useState({
        name: '', sku: '', barcode: '', externalReference: '', description: '',
        price: 0, quantity: 0, minStock: 5, imageUrl: '',
        shelfLocation: '', categoryId: '', warehouseId: '',
        locationIds: [],
        unitsPerBox: 1, boxesPerPalette: 1,
        status: 'Actif'
    });
    const [locations, setLocations] = useState([]);



    const fetchMetadata = useCallback(async () => {
        if (!user?.companyId) return;
        try {
            const [catRes, whRes, supRes] = await Promise.all([
                fetch(`/api/categories?companyId=${user.companyId}`),
                fetch(`/api/warehouses?companyId=${user.companyId}`),
                fetch(`/api/suppliers?companyId=${user.companyId}&limit=999`)
            ]);

            if (catRes.ok) {
                const catData = await catRes.json();
                setCategories(catData);
                categoriesRef.current = catData;
            }
            if (whRes.ok) {
                const whData = await whRes.json();
                setWarehouses(whData);
                warehousesRef.current = whData;
            }
            if (supRes.ok) {
                const supData = await supRes.json();
                setSuppliers(supData.suppliers || []);
            }
        } catch (error) {
            console.error("Error fetching metadata:", error);
        }
    }, [user?.companyId]);

    const fetchData = useCallback(async () => {
        if (!user?.companyId) return;
        try {
            const queryParams = new URLSearchParams({
                companyId: user.companyId,
                search: search,
                status: filterStatus !== 'Tous' ? filterStatus : '',
                page: currentPage,
                limit: limit,
                sortBy: sortBy,
                sortOrder: sortOrder
            });

            if (filterCategory !== 'Tous') {
                if (filterCategory === 'Sans catégorie') {
                    queryParams.append('categoryId', 'null');
                } else {
                    const cat = categoriesRef.current.find(c => c.name === filterCategory);
                    if (cat) queryParams.append('categoryId', cat.id);
                }
            }

            if (filterWarehouse !== 'Tous') {
                if (filterWarehouse === 'Non assignés') {
                    queryParams.append('warehouseId', 'null');
                } else {
                    const wh = warehousesRef.current.find(w => w.name === filterWarehouse);
                    if (wh) queryParams.append('warehouseId', wh.id);
                }
            }

            const [prodRes, statsRes] = await Promise.all([
                fetch(`/api/products?${queryParams.toString()}`),
                fetch(`/api/products/stats?companyId=${user.companyId}`)
            ]);

            if (prodRes.ok) {
                const data = await prodRes.json();
                setProducts(data.products);
                setTotalPages(data.totalPages);
                setTotalProducts(data.total);
            }
            if (statsRes.ok) setStats(await statsRes.json());
        } catch (error) {
            console.error(error);
            showToast("Erreur lors du chargement des données", "error");
        }
    }, [user?.companyId, search, filterCategory, filterStatus, filterWarehouse, currentPage, limit, sortBy, sortOrder, showToast]);

    useEffect(() => {
        fetchMetadata();
    }, [fetchMetadata]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (!socket) return;

        const handleUpdate = () => {
            fetchData();
        };

        socket.on('product_updated', handleUpdate);
        socket.on('stock_updated', handleUpdate);

        return () => {
            socket.off('product_updated', handleUpdate);
            socket.off('stock_updated', handleUpdate);
        };
    }, [socket, fetchData]);

    // Reset pagination on filter/search change
    useEffect(() => {
        setCurrentPage(1);
    }, [search, filterCategory, filterStatus, filterWarehouse]);

    useEffect(() => {
        const fetchLocations = async () => {
            if (!formData.warehouseId || !user?.companyId) {
                setLocations([]);
                return;
            }
            try {
                const res = await fetch(`/api/locations/with-path?warehouseId=${formData.warehouseId}&companyId=${user.companyId}`);
                if (res.ok) {
                    const all = await res.json();
                    setLocations(all.filter(l => l.type === 'Slot'));
                }
            } catch (e) { console.error(e); }
        };
        fetchLocations();
    }, [formData.warehouseId, user?.companyId]);



    const handleReset = () => {
        setSearch('');
        setFilterCategory('Tous');
        setFilterStatus('Tous');
        setFilterWarehouse('Tous');
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingProduct ? `/api/products/${editingProduct.id}` : '/api/products';
            const method = editingProduct ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...formData, companyId: user.companyId, userId: user.id })
            });

            if (res.ok) {
                showToast(editingProduct ? 'Produit mis à jour' : 'Produit créé', 'success');
                setShowModal(false);
                fetchData();
            } else {
                const error = await res.json();
                showToast(error.error || "Erreur lors de la sauvegarde", "error");
            }
        } catch (error) {
            showToast("Erreur lors de la sauvegarde", "error");
        }
    };

    const handleImport = async (data) => {
        try {
            const res = await fetch('/api/products/bulk', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    products: data,
                    companyId: user.companyId,
                    userId: user.id
                })
            });

            const result = await res.json();

            if (res.ok) {
                fetchData();
                return result;
            } else {
                showToast(result.error || "Erreur lors de l'importation", "error");
                throw new Error(result.error);
            }
        } catch (error) {
            showToast("Erreur lors de l'importation", "error");
            throw error;
        }
    };

    const handleBulkUpdate = async (updateData) => {
        if (selectedIds.length === 0) return;
        try {
            const res = await fetch('/api/products/bulk-update', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ids: selectedIds,
                    data: updateData,
                    companyId: user.companyId,
                    userId: user.id
                })
            });
            if (res.ok) {
                showToast(`${selectedIds.length} produits mis à jour`, 'success');
                setSelectedIds([]);
                fetchData();
            } else {
                const error = await res.json();
                showToast(error.error || "Erreur lors de la mise à jour", "error");
            }
        } catch (error) {
            showToast("Erreur lors de la mise à jour", "error");
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        showConfirm({
            title: `Supprimer ${selectedIds.length} produits ?`,
            message: `Attention : cette action est irréversible. Cela supprimera définitivement les produits sélectionnés ainsi que TOUT leur historique associé (mouvements de stock, commandes, etc.).`,
            type: 'danger',
            onConfirm: async () => {
                try {
                    const res = await fetch('/api/products/bulk-delete', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            ids: selectedIds,
                            companyId: user.companyId
                        })
                    });
                    if (res.ok) {
                        showToast(`${selectedIds.length} produits supprimés`, 'success');
                        setSelectedIds([]);
                        fetchData();
                    } else {
                        const error = await res.json();
                        showToast(error.error || "Erreur lors de la suppression", "error");
                    }
                } catch (error) {
                    showToast("Erreur lors de la suppression", "error");
                }
            }
        });
    };

    const handleQuickUpdate = async (productId, field, newValue) => {
        const product = products.find(p => p.id === productId);
        if (!product || product[field] === newValue) return;

        // Mise à jour locale immédiate pour la fluidité
        setProducts(prev => prev.map(p => p.id === productId ? { ...p, [field]: newValue } : p));

        try {
            const res = await fetch(`/api/products/${productId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ [field]: newValue, companyId: user.companyId, userId: user.id })
            });

            if (res.ok) {
                // Rafraîchir les stats mais pas les produits (déjà fait localement)
                const statsRes = await fetch(`/api/products/stats?companyId=${user.companyId}`);
                if (statsRes.ok) setStats(await statsRes.json());
            } else {
                const error = await res.json();
                showToast(error.error || "Erreur lors de la mise à jour", "error");
                fetchData(); // Reset fallback
            }
        } catch (error) {
            showToast("Erreur lors de la mise à jour", "error");
            fetchData(); // Reset fallback
        }
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === products.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(products.map(p => p.id));
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const importFields = [
        { key: 'name', label: 'Nom', required: true },
        { key: 'sku', label: 'SKU', required: true },
        { key: 'barcode', label: 'Code EAN' },
        { key: 'price', label: 'Prix' },
        { key: 'quantity', label: 'Quantité' },
        { key: 'minStock', label: 'Seuil Alerte' },
        { key: 'description', label: 'Description' },
        { key: 'externalReference', label: 'Référence Externe' },
        { key: 'category', label: 'Catégorie' },
        { key: 'warehouse', label: 'Entrepôt' },
        { key: 'unitsPerBox', label: 'Unités/Boîte' },
        { key: 'boxesPerPalette', label: 'Boîtes/Palette' },
    ];

    const handleExportExcel = async () => {
        try {
            showToast("Préparation de l'export...", "info");

            const allProducts = products || [];

            if (allProducts.length === 0) {
                showToast("Aucun produit à exporter", "warning");
                return;
            }

            const exportData = allProducts.map(p => ({
                'SKU': p.sku || '',
                'Nom': p.name || '',
                'Description': p.description || '',
                'Référence Externe': p.externalReference || '',
                'Catégorie': p.category?.name || '',
                'Entrepôt': p.warehouse?.name || '',
                'Quantité': p.quantity ?? 0,
                'Seuil Alerte': p.minStock ?? 0,
                'Prix': p.price ? parseFloat(p.price) : 0,
                'Unités/Boîte': p.unitsPerBox ?? 1,
                'Boîtes/Palette': p.boxesPerPalette ?? 1,
                'Statut': p.quantity === 0 ? 'Rupture' : (p.quantity <= p.minStock ? 'Stock Faible' : 'En Stock'),
            }));

            const ws = XLSX.utils.json_to_sheet(exportData);
            // Set column widths
            ws['!cols'] = [
                { wch: 15 }, { wch: 30 }, { wch: 40 }, { wch: 20 },
                { wch: 15 }, { wch: 15 }, { wch: 10 }, { wch: 12 },
                { wch: 10 }, { wch: 12 }, { wch: 14 }, { wch: 12 }
            ];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Inventaire');

            const date = new Date().toISOString().slice(0, 10);
            XLSX.writeFile(wb, `Inventaire_${date}.xlsx`);
            showToast(`${allProducts.length} produits exportés avec succès`, "success");
        } catch (err) {
            console.error('Export error:', err);
            showToast("Erreur lors de l'export", "error");
        }
    };

    const handleDelete = (product) => {
        if (product.quantity > 0) {
            showToast(
                `Impossible de supprimer "${product.name}" : le stock est de ${product.quantity} unité(s). Ramenez le stock à 0 avant de supprimer.`,
                'error'
            );
            return;
        }
        showConfirm({
            title: 'Supprimer ce produit ?',
            message: 'Attention : cette action est irréversible. Cela supprimera définitivement le produit ainsi que TOUT son historique associé (mouvements de stock, commandes, etc.).',
            type: 'danger',
            onConfirm: async () => {
                await fetch(`/api/products/${product.id}`, { method: 'DELETE' });
                showToast('Produit supprimé', 'success');
                fetchData();
            }
        });
    };

    const openEdit = (p) => {
        setEditingProduct(p);
        setFormData({
            name: p.name,
            sku: p.sku,
            barcode: p.barcode || '',
            externalReference: p.externalReference || '',
            description: p.description || '',
            price: p.price,
            quantity: p.quantity,
            minStock: p.minStock,
            imageUrl: p.imageUrl || '',
            shelfLocation: p.shelfLocation || '',
            locationIds: p.locations ? p.locations.map(l => l.id) : [],
            categoryId: p.categoryId || '',
            warehouseId: p.warehouseId || '',

            unitsPerBox: p.unitsPerBox || 1,
            boxesPerPalette: p.boxesPerPalette || 1,
            status: p.status || 'Actif'
        });

        setShowModal(true);
    };

    const getStatusColor = (p) => {
        if (p.quantity === 0) return 'rose';
        if (p.quantity <= p.minStock) return 'amber';
        return 'emerald';
    };

    const getStockPercentage = (p) => {
        if (p.quantity === 0) return 0;
        const max = Math.max(p.minStock * 2, 10);
        return Math.min(Math.round((p.quantity / max) * 100), 100);
    };

    const getStockDecomposition = (p) => {
        if (!p.unitsPerBox || p.unitsPerBox <= 1) return null;

        let remaining = p.quantity;
        const unitsPerPalette = p.unitsPerBox * (p.boxesPerPalette || 1);

        const palettes = Math.floor(remaining / unitsPerPalette);
        remaining %= unitsPerPalette;

        const boxes = Math.floor(remaining / p.unitsPerBox);
        remaining %= p.unitsPerBox;

        const parts = [];
        if (palettes > 0) parts.push(`${palettes} pal.`);
        if (boxes > 0) parts.push(`${boxes} cart.`);
        if (remaining > 0 || parts.length === 0) parts.push(`${remaining} un.`);

        return parts.join(', ');
    };


    return (
        <div className="flex flex-col">
            {/* Header Section - Sticky with compensation for MainLayout padding */}
            <div className="sticky top-[-16px] lg:top-[-16px] z-30 bg-background-light/95 dark:bg-background-dark/95 backdrop-blur-sm pt-4 pb-6 mb-2 border-b border-slate-200/50 dark:border-slate-800/50">
                <div className="flex flex-wrap justify-between items-end gap-4 max-w-[1600px] mx-auto w-full">
                    <div className="flex flex-col gap-1">
                        <h1 className="text-3xl font-black leading-tight tracking-tight bg-gradient-to-r from-primary to-blue-500 bg-clip-text text-transparent drop-shadow-sm">Inventaire</h1>
                        <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Suivi en temps réel de votre stock et mapping CRM.</p>
                    </div>
                    <div className="flex gap-3">
                        <PermissionGuard permission="inv.export">
                            <button
                                onClick={handleExportExcel}
                                className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-700 transition-all border border-slate-200 dark:border-slate-700 shadow-sm"
                            >
                                <span className="material-symbols-outlined text-[20px]">file_download</span>
                                <span className="truncate">Exporter</span>
                            </button>
                        </PermissionGuard>
                        <PermissionGuard permission="inv.import">
                            <button
                                onClick={() => setShowImportModal(true)}
                                className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-700 transition-all border border-slate-200 dark:border-slate-700 shadow-sm"
                            >
                                <span className="material-symbols-outlined text-[20px]">upload_file</span>
                                <span className="truncate">Importer</span>
                            </button>
                        </PermissionGuard>
                        <PermissionGuard permission="inv.manage">
                            <button
                                onClick={() => {
                                    setEditingProduct(null);
                                    setFormData({
                                        name: '', sku: '', barcode: '', externalReference: '', description: '',
                                        price: 0, quantity: 0, minStock: 10, imageUrl: '',
                                        shelfLocation: '', categoryId: '',
                                        warehouseId: '',
                                        locationIds: [],
                                        unitsPerBox: 1, boxesPerPalette: 1,
                                        status: 'Actif'
                                    });

                                    setShowModal(true);
                                }}

                                className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
                            >
                                <span className="material-symbols-outlined text-[20px]">add</span>
                                <span className="truncate">Nouveau Produit</span>
                            </button>
                        </PermissionGuard>
                    </div>
                </div>
            </div>

            <div className="space-y-8 pt-4">
                {/* Filters & Table Container */}
                <div className="bg-white dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden backdrop-blur-sm shadow-sm">
                    <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex flex-wrap gap-4 items-center justify-between">
                        <div className="flex flex-wrap gap-3">
                            <div className="relative">
                                <select
                                    className="appearance-none h-10 bg-slate-100 dark:bg-slate-800 border border-transparent rounded-lg px-4 pr-10 text-sm font-medium focus:ring-1 focus:ring-primary cursor-pointer outline-none transition-colors hover:bg-slate-200 dark:hover:bg-slate-700"
                                    value={filterCategory}
                                    onChange={(e) => setFilterCategory(e.target.value)}
                                >
                                    <option value="Tous">Toutes catégories</option>
                                    <option value="Sans catégorie">Général (aucune)</option>
                                    {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                                </select>
                                <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-[18px] pointer-events-none">keyboard_arrow_down</span>
                            </div>
                            <div className="relative">
                                <select
                                    className="appearance-none h-10 bg-slate-100 dark:bg-slate-800 border border-transparent rounded-lg px-4 pr-10 text-sm font-medium focus:ring-1 focus:ring-primary cursor-pointer outline-none transition-colors hover:bg-slate-200 dark:hover:bg-slate-700"
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                >
                                    <option value="Tous">Statut: Tous</option>
                                    <option value="En Stock">Statut: En Stock</option>
                                    <option value="Stock Faible">Statut: Stock Faible</option>
                                    <option value="Rupture">Statut: Rupture</option>
                                    <option value="Brouillon">Statut: Brouillon</option>
                                    <option value="Actif">Statut: Validé / Actif</option>
                                </select>
                                <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-[18px] pointer-events-none">keyboard_arrow_down</span>
                            </div>
                            <div className="relative">
                                <select
                                    className="appearance-none h-10 bg-slate-100 dark:bg-slate-800 border border-transparent rounded-lg px-4 pr-10 text-sm font-medium focus:ring-1 focus:ring-primary cursor-pointer outline-none transition-colors hover:bg-slate-200 dark:hover:bg-slate-700"
                                    value={filterWarehouse}
                                    onChange={(e) => setFilterWarehouse(e.target.value)}
                                >
                                    <option value="Tous">Tous les entrepôts</option>
                                    <option value="Non assignés">Sans entrepôt</option>
                                    {warehouses.map(w => <option key={w.id} value={w.name}>{w.name}</option>)}
                                </select>
                                <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-[18px] pointer-events-none">keyboard_arrow_down</span>
                            </div>

                            <div className="relative">
                                <button onClick={handleReset} className="h-10 px-4 text-primary text-sm font-semibold hover:bg-primary/10 rounded-lg transition-colors">Réinitialiser</button>
                            </div>

                        </div>
                        <div className="flex-1 max-w-md">
                            <div className="relative">
                                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[20px]">search</span>
                                <input
                                    className="form-input w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary outline-none transition-all"
                                    placeholder="Rechercher par nom ou SKU..."
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse table-fixed min-w-[1200px]">
                            <thead>
                                <tr className="bg-slate-50/50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 text-xs font-semibold uppercase tracking-wider">
                                    <th className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 w-[50px]">
                                        <input
                                            type="checkbox"
                                            tabIndex="-1"
                                            className="size-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
                                            checked={products.length > 0 && selectedIds.length === products.length}
                                            onChange={toggleSelectAll}
                                        />
                                    </th>
                                    <SortableHeader label="Produit" field="name" currentSort={sortBy} currentOrder={sortOrder} onSort={(f, o) => { setSortBy(f); setSortOrder(o); }} className="w-[20%]" />
                                    <SortableHeader label="SKU" field="sku" currentSort={sortBy} currentOrder={sortOrder} onSort={(f, o) => { setSortBy(f); setSortOrder(o); }} className="w-[12%]" />
                                    <th className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 w-[12%]">Catégorie</th>
                                    <th className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 w-[12%]">Entrepôt</th>

                                    <th className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 text-center w-[12%]">Niveau</th>
                                    <th className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 text-center w-[10%]">
                                        <div className="flex flex-col items-center">
                                            <SortableHeader as="div" label="Qté" field="quantity" currentSort={sortBy} currentOrder={sortOrder} onSort={(f, o) => { setSortBy(f); setSortOrder(o); }} noPadding />
                                            <span className="text-[9px] text-slate-400 font-bold uppercase tracking-tighter">Autonomie</span>
                                        </div>
                                    </th>
                                    <SortableHeader label="Prix" field="price" currentSort={sortBy} currentOrder={sortOrder} onSort={(f, o) => { setSortBy(f); setSortOrder(o); }} className="text-center w-[8%]" />
                                    {/* Actions Header - only if manage or delete */}
                                    <PermissionGuard permission="inv.manage" fallback={
                                        <PermissionGuard permission="inv.delete">
                                            <th className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 text-right w-[11%]">Actions</th>
                                        </PermissionGuard>
                                    }>
                                        <th className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 text-right w-[11%]">Actions</th>
                                    </PermissionGuard>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                                {products.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-12 text-center text-slate-400 italic">
                                            Aucun produit trouvé.
                                        </td>
                                    </tr>
                                ) : products.map(p => (
                                    <tr key={p.id} className={`${selectedIds.includes(p.id) ? 'bg-primary/5 dark:bg-primary/10' : ''} hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors group`}>
                                        <td className="px-6 py-4 border-b border-slate-200 dark:border-slate-800">
                                            <input
                                                type="checkbox"
                                                tabIndex="-1"
                                                className="size-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
                                                checked={selectedIds.includes(p.id)}
                                                onChange={() => toggleSelect(p.id)}
                                            />
                                        </td>
                                        <td className={`px-6 py-4 border-b border-slate-200 dark:border-slate-800 ${hasPermission('inv.manage') ? 'cursor-pointer' : ''}`} onClick={() => hasPermission('inv.manage') && openEdit(p)}>
                                            <div className="flex items-center gap-3">
                                                <div className="size-10 rounded bg-slate-100 dark:bg-slate-800 flex-shrink-0 overflow-hidden border border-slate-200 dark:border-slate-700">
                                                    <ProductImage src={p.imageUrl} name={p.name} />
                                                </div>
                                                <div className="flex flex-col">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{p.name}</span>
                                                        {p.status === 'Brouillon' && (
                                                            <span className="px-1.5 py-0.5 text-[9px] font-black bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 rounded uppercase tracking-tighter">Brouillon</span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs text-slate-500">{p.externalReference || 'Pas de réf CRM'}</span>
                                                        {p.barcode && <span className="text-[10px] text-slate-400 bg-slate-100 dark:bg-slate-800 px-1 rounded font-mono">EAN: {p.barcode}</span>}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className={`px-6 py-4 text-sm font-mono text-slate-600 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800 ${hasPermission('inv.manage') ? 'cursor-pointer' : ''}`} onClick={() => hasPermission('inv.manage') && openEdit(p)}>{p.sku}</td>
                                        <td className="px-6 py-4 border-b border-slate-200 dark:border-slate-800">
                                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                                                {p.category?.name || 'Général'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 border-b border-slate-200 dark:border-slate-800">
                                            <span className="text-xs font-bold text-slate-600 dark:text-slate-400">
                                                {p.warehouse?.name || 'Inconnu'}
                                            </span>
                                        </td>

                                        <td className="px-6 py-4 border-b border-slate-200 dark:border-slate-800">
                                            <div className="flex flex-col items-center gap-1.5 mx-auto">
                                                <div className="w-full bg-slate-200 dark:bg-slate-700 h-1.5 rounded-full overflow-hidden max-w-[100px]">
                                                    <div className={`h-full transition-all duration-500 bg-${getStatusColor(p)}-500`} style={{ width: `${getStockPercentage(p)}%` }}></div>
                                                </div>
                                                <span className={`text-[10px] font-bold text-${getStatusColor(p)}-600 dark:text-${getStatusColor(p)}-400 uppercase`}>
                                                    {p.quantity === 0 ? 'Rupture' : p.quantity <= p.minStock ? 'Faible' : 'OK'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 border-b border-slate-200 dark:border-slate-800">
                                            <div className="flex flex-col items-center gap-1 mx-auto">
                                                {p.autonomy != null && (
                                                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded border uppercase tracking-tighter mb-1 ${p.autonomy <= 3 ? 'bg-rose-50 text-rose-600 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800' :
                                                        p.autonomy <= 7 ? 'bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800' :
                                                            'bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800'
                                                        }`}>
                                                        {p.autonomy === 0 ? 'RUPTURE' : `${p.autonomy} JOURS`}
                                                    </span>
                                                )}
                                                {hasPermission('inv.manage') ? (
                                                    <QuickEditInput
                                                        type="number"
                                                        initialValue={p.quantity}
                                                        onSave={(val) => handleQuickUpdate(p.id, 'quantity', Number(val))}
                                                        className="w-20 text-center bg-slate-50 dark:bg-slate-800/50 border border-transparent hover:border-slate-300 dark:hover:border-slate-600 focus:border-primary focus:ring-1 focus:ring-primary rounded-lg py-1 px-2 text-sm font-bold outline-none transition-all"
                                                    />
                                                ) : (
                                                    <span className="text-sm font-bold text-slate-700 dark:text-slate-300 inline-block w-20 text-center py-1">
                                                        {p.quantity}
                                                    </span>
                                                )}

                                                {p.unitsPerBox > 1 && (
                                                    <span className="text-[10px] text-slate-400 font-medium whitespace-nowrap">
                                                        {getStockDecomposition(p)}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 text-center">
                                            <div className="flex items-center justify-center">
                                                {hasPermission('inv.manage') ? (
                                                    <QuickEditInput
                                                        type="text"
                                                        initialValue={p.price}
                                                        onSave={(val) => {
                                                            const cleanVal = String(val).replace(',', '.');
                                                            handleQuickUpdate(p.id, 'price', parseFloat(cleanVal) || 0);
                                                        }}
                                                        className="w-24 text-center bg-slate-50 dark:bg-slate-800/50 border border-transparent hover:border-slate-300 dark:hover:border-slate-600 focus:border-primary focus:ring-1 focus:ring-primary rounded-lg py-1 px-2 text-sm font-bold outline-none transition-all"
                                                    />
                                                ) : (
                                                    <span className="text-sm font-bold text-slate-700 dark:text-slate-300 inline-block w-24 text-center py-1">
                                                        {p.price}
                                                    </span>
                                                )}

                                                <span className="text-sm font-bold text-slate-400 ml-1">€</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 text-right">
                                            <div className="flex justify-end gap-1 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
                                                <PermissionGuard permission="inv.manage">
                                                    <button onClick={() => openEdit(p)} tabIndex="-1" className="p-2 text-slate-400 hover:text-primary transition-colors">
                                                        <span className="material-symbols-outlined text-[20px]">edit</span>
                                                    </button>
                                                </PermissionGuard>
                                                <PermissionGuard permission="inv.delete">
                                                    <button
                                                        onClick={() => handleDelete(p)}
                                                        tabIndex="-1"
                                                        disabled={p.quantity > 0}
                                                        title={p.quantity > 0 ? `Stock: ${p.quantity} unité(s) — ramenez à 0 pour supprimer` : 'Supprimer le produit'}
                                                        className={`p-2 transition-colors ${p.quantity > 0 ? 'text-slate-200 dark:text-slate-700 cursor-not-allowed' : 'text-slate-400 hover:text-rose-500'}`}
                                                    >
                                                        <span className="material-symbols-outlined text-[20px]">delete</span>
                                                    </button>
                                                </PermissionGuard>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        limit={limit}
                        onLimitChange={(l) => { setLimit(l); setCurrentPage(1); }}
                        totalItems={totalProducts}
                    />
                </div>

                {/* Statistics Section (at the bottom as per V2) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-900/30 p-5 rounded-xl shadow-sm">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="material-symbols-outlined text-emerald-600 dark:text-emerald-400">check_circle</span>
                            <span className="text-sm font-semibold text-emerald-800 dark:text-emerald-300 uppercase tracking-wider">Total Produits</span>
                        </div>
                        <p className="text-2xl font-black text-emerald-900 dark:text-emerald-100">{stats?.total || 0}</p>
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">Gérés par {user?.companyName || 'votre entreprise'}</p>
                    </div>
                    <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30 p-5 rounded-xl shadow-sm">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="material-symbols-outlined text-amber-600 dark:text-amber-400">warning</span>
                            <span className="text-sm font-semibold text-amber-800 dark:text-amber-300 uppercase tracking-wider">Réapprovisionnement</span>
                        </div>
                        <p className="text-2xl font-black text-amber-900 dark:text-amber-100">{stats?.lowStock || 0}</p>
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Articles à commander d'urgence</p>
                    </div>
                    <div className="bg-rose-50 dark:bg-rose-900/10 border border-rose-100 dark:border-rose-900/30 p-5 rounded-xl shadow-sm">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="material-symbols-outlined text-rose-600 dark:text-rose-400">error</span>
                            <span className="text-sm font-semibold text-rose-800 dark:text-rose-300 uppercase tracking-wider">Rupture Stock</span>
                        </div>
                        <p className="text-2xl font-black text-rose-900 dark:text-rose-100">{stats?.outOfStock || 0}</p>
                        <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">Ruptures totales</p>
                    </div>
                    {stats?.drafts > 0 && (
                        <div className="bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 p-5 rounded-xl shadow-sm md:col-span-3 lg:col-span-1">
                            <div className="flex items-center gap-3 mb-3">
                                <span className="material-symbols-outlined text-amber-500">pending_actions</span>
                                <span className="text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Brouillons à valider</span>
                            </div>
                            <p className="text-2xl font-black text-slate-900 dark:text-white">{stats?.drafts || 0}</p>
                            <p className="text-xs text-slate-500 mt-1">Produits créés via scanner mobile</p>
                        </div>
                    )}
                </div>

                {/* Premium Slide-over Panel */}
                {showModal && (
                    <div className="fixed inset-0 bg-black/60 z-[200] flex items-stretch justify-end backdrop-blur-sm" style={{ animation: 'fadeIn 200ms ease forwards' }}>
                        {/* Click-away backdrop */}
                        <div className="flex-1" onClick={() => setShowModal(false)} />

                        {/* Panel */}
                        <div className="bg-white dark:bg-slate-900 w-full max-w-2xl flex flex-col shadow-2xl border-l border-slate-200 dark:border-slate-800 rounded-l-2xl overflow-hidden" style={{ animation: 'slideInFromRight 300ms cubic-bezier(0.32, 0.72, 0, 1) forwards' }}>

                            {/* Sticky Header */}
                            <div className="shrink-0 px-8 py-6 flex items-center justify-between bg-gradient-to-br from-primary/10 via-primary/5 to-transparent dark:from-primary/20 dark:via-primary/10 dark:to-transparent border-b border-primary/10 dark:border-primary/20 sticky top-0 z-10">
                                <div className="flex items-center gap-4">
                                    <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center">
                                        <span className="material-symbols-outlined text-primary text-[22px]">{editingProduct ? 'edit' : 'add_box'}</span>
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-black text-slate-900 dark:text-white leading-tight">
                                            {editingProduct ? formData.name || 'Modifier le produit' : 'Nouveau Produit'}
                                        </h3>
                                        <p className="text-xs text-slate-400 font-medium mt-0.5">
                                            {editingProduct ? `SKU: ${formData.sku}` : 'Remplissez les informations ci-dessous'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    {/* Status badge inline */}
                                    <select
                                        className={`h-8 px-3 rounded-full text-xs font-black uppercase tracking-wider border-2 outline-none cursor-pointer transition-all ${formData.status === 'Actif' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800' :
                                            formData.status === 'Brouillon' ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800' :
                                                'bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:border-slate-700'
                                            }`}
                                        value={formData.status}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    >
                                        <option value="Actif">✓ Actif</option>
                                        <option value="Brouillon">⏳ Brouillon</option>
                                        <option value="Archivé">📦 Archivé</option>
                                    </select>
                                    <button
                                        onClick={() => setShowModal(false)}
                                        className="size-9 flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
                                        title="Fermer"
                                    >
                                        <span className="material-symbols-outlined text-[22px]">close</span>
                                    </button>
                                </div>
                            </div>

                            {/* Scrollable body */}
                            <div className="flex-1 overflow-y-auto">
                                <form onSubmit={handleSubmit} id="product-form">

                                    {/* Image + SKU Hero Section */}
                                    <div className="px-8 pt-8 pb-0 flex gap-6 items-start">
                                        {/* Image preview */}
                                        <div className="shrink-0">
                                            <div className="size-24 rounded-2xl bg-slate-100 dark:bg-slate-800 border-2 border-dashed border-slate-200 dark:border-slate-700 overflow-hidden relative group/img cursor-pointer shadow-inner"
                                                onClick={() => {
                                                    if (formData.imageUrl) {
                                                        const resolvedUrl = resolveImageUrl(formData.imageUrl);
                                                        if (resolvedUrl) window.open(resolvedUrl, '_blank');
                                                    }
                                                }}
                                                title={formData.imageUrl ? "Voir en grand" : "Aperçu de l'image"}
                                            >
                                                {formData.imageUrl ? (
                                                    <>
                                                        <ProductImage src={formData.imageUrl} name="Aperçu" className="w-full h-full object-cover" />
                                                        <button
                                                            type="button"
                                                            onClick={(e) => { e.stopPropagation(); setFormData({ ...formData, imageUrl: '' }); }}
                                                            className="absolute inset-0 bg-black/50 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center text-white"
                                                        >
                                                            <span className="material-symbols-outlined text-[22px]">delete</span>
                                                        </button>
                                                    </>
                                                ) : (
                                                    <div className="w-full h-full flex flex-col items-center justify-center text-slate-300 dark:text-slate-600 gap-1">
                                                        <span className="material-symbols-outlined text-4xl">image</span>
                                                        <span className="text-[9px] font-bold uppercase tracking-wider">Photo</span>
                                                    </div>
                                                )}
                                            </div>
                                            <p className="text-[9px] text-slate-400 text-center mt-1.5 font-medium">Collé via URL</p>
                                        </div>

                                        {/* Name + SKU */}
                                        <div className="flex-1 space-y-3">
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Nom du Produit *</label>
                                                <input
                                                    required
                                                    className="w-full bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-xl px-4 py-2.5 text-base font-bold outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all placeholder:text-slate-300 dark:placeholder:text-slate-600"
                                                    value={formData.name}
                                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                    placeholder="ex: Montre Chrono Silver"
                                                />
                                            </div>
                                            <div className="flex gap-3 items-start">
                                                <div className="flex-1">
                                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">SKU *</label>
                                                    <input
                                                        required
                                                        className="w-full bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-xl px-4 py-2.5 text-sm font-mono font-bold outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all"
                                                        value={formData.sku}
                                                        onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                                                        placeholder="WAT-SLV-001"
                                                    />
                                                </div>
                                                {formData.sku && (
                                                    <div className="mt-6 p-1.5 bg-white border border-slate-200 rounded-lg shadow-sm shrink-0" title="QR Code SKU">
                                                        <QRCodeSVG value={formData.sku} size={40} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Image URL field */}
                                    <div className="px-8 pt-4">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">URL de l'image</label>
                                        <input
                                            className="w-full bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-xl px-4 py-2.5 text-xs font-mono outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all text-slate-500"
                                            value={formData.imageUrl}
                                            onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                                            placeholder="https://... ou /uploads/products/..."
                                        />
                                    </div>

                                    {/* Divider section: Identification */}
                                    <div className="px-8 pt-8">
                                        <div className="flex items-center gap-3 mb-5">
                                            <span className="material-symbols-outlined text-primary text-[18px]">fingerprint</span>
                                            <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.15em]">Identification & Références</h4>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Code EAN / Barcode</label>
                                                <input
                                                    className="w-full bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-xl px-4 py-3 text-sm font-mono outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all"
                                                    value={formData.barcode}
                                                    onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                                                    placeholder="3017620422003"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Réf. CRM / Externe</label>
                                                <input
                                                    className="w-full bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all"
                                                    value={formData.externalReference}
                                                    onChange={(e) => setFormData({ ...formData, externalReference: e.target.value })}
                                                    placeholder="Id CRM ou Marketplace"
                                                />
                                            </div>
                                            <div className="col-span-2">
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Description</label>
                                                <textarea
                                                    rows="3"
                                                    className="w-full bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 resize-none transition-all"
                                                    value={formData.description}
                                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                    placeholder="Description détaillée du produit..."
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Divider section: Prix & Stock */}
                                    <div className="px-8 pt-8">
                                        <div className="flex items-center gap-3 mb-5">
                                            <span className="material-symbols-outlined text-emerald-500 text-[18px]">inventory_2</span>
                                            <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.15em]">Stock & Prix</h4>
                                        </div>
                                        <div className="grid grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Quantité</label>
                                                <input
                                                    type="number"
                                                    className="w-full bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-xl px-4 py-3 text-sm font-bold text-center outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-400/50 transition-all"
                                                    value={formData.quantity}
                                                    onChange={(e) => setFormData({ ...formData, quantity: Number(e.target.value) })}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Seuil Alerte</label>
                                                <input
                                                    type="number"
                                                    className="w-full bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-xl px-4 py-3 text-sm font-bold text-center outline-none focus:ring-2 focus:ring-amber-400/30 focus:border-amber-400/50 transition-all"
                                                    value={formData.minStock}
                                                    onChange={(e) => setFormData({ ...formData, minStock: Number(e.target.value) })}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Prix de vente (€)</label>
                                                <input
                                                    type="text"
                                                    className="w-full bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-xl px-4 py-3 text-sm font-bold font-mono text-center outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all"
                                                    value={formData.price}
                                                    onChange={(e) => {
                                                        const val = e.target.value.replace(',', '.');
                                                        setFormData({ ...formData, price: val });
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Conditionnement */}
                                        <div className="mt-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Conditionnement</p>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-xs text-slate-500 font-semibold mb-1.5">Unités par Carton</label>
                                                    <input
                                                        type="number"
                                                        className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm font-bold text-center outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                                                        value={formData.unitsPerBox}
                                                        onChange={(e) => setFormData({ ...formData, unitsPerBox: Number(e.target.value) })}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs text-slate-500 font-semibold mb-1.5">Cartons par Palette</label>
                                                    <input
                                                        type="number"
                                                        className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm font-bold text-center outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                                                        value={formData.boxesPerPalette}
                                                        onChange={(e) => setFormData({ ...formData, boxesPerPalette: Number(e.target.value) })}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Divider section: Localisation */}
                                    <div className="px-8 pt-8 pb-8">
                                        <div className="flex items-center gap-3 mb-5">
                                            <span className="material-symbols-outlined text-violet-500 text-[18px]">location_on</span>
                                            <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.15em]">Catégorie & Entrepôt</h4>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Catégorie</label>
                                                <div className="relative">
                                                    <select
                                                        required
                                                        className={`w-full appearance-none bg-slate-50 dark:bg-slate-800 border-2 rounded-xl px-4 py-3 pr-10 text-sm font-bold outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all cursor-pointer ${!formData.categoryId ? 'border-amber-300/80 dark:border-amber-700/80' : 'border-transparent'}`}
                                                        value={formData.categoryId}
                                                        onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                                                    >
                                                        <option value="">-- Choisir --</option>
                                                        <option value="NULL">Général (Aucune)</option>
                                                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                                    </select>
                                                    <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px] pointer-events-none">expand_more</span>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Entrepôt</label>
                                                <div className="relative">
                                                    <select
                                                        required
                                                        className={`w-full appearance-none bg-slate-50 dark:bg-slate-800 border-2 rounded-xl px-4 py-3 pr-10 text-sm font-bold outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all cursor-pointer ${!formData.warehouseId ? 'border-amber-300/80 dark:border-amber-700/80' : 'border-transparent'}`}
                                                        value={formData.warehouseId}
                                                        onChange={(e) => setFormData({ ...formData, warehouseId: e.target.value })}
                                                    >
                                                        <option value="">-- Choisir --</option>
                                                        {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                                                    </select>
                                                    <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px] pointer-events-none">expand_more</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Locations */}
                                        {formData.warehouseId && locations.length > 0 && (
                                            <div className="mt-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Emplacements</label>
                                                {formData.locationIds.length > 0 && (
                                                    <div className="flex flex-wrap gap-2 mb-3">
                                                        {formData.locationIds.map(locId => {
                                                            const loc = locations.find(l => l.id === locId);
                                                            if (!loc) return null;
                                                            return (
                                                                <span key={locId} className="inline-flex items-center gap-1.5 bg-primary/10 text-primary border border-primary/20 rounded-lg px-3 py-1.5 text-xs font-bold">
                                                                    <span className="material-symbols-outlined text-xs">location_on</span>
                                                                    {loc.fullPath}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setFormData({ ...formData, locationIds: formData.locationIds.filter(id => id !== locId) })}
                                                                        className="ml-1 text-primary/60 hover:text-red-500 transition-colors"
                                                                    >
                                                                        <span className="material-symbols-outlined text-sm">close</span>
                                                                    </button>
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                                <div className="relative">
                                                    <select
                                                        className="w-full appearance-none bg-slate-50 dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-xl px-4 py-3 pr-10 text-sm outline-none focus:ring-2 focus:ring-primary/30 transition-all cursor-pointer"
                                                        value=""
                                                        onChange={(e) => {
                                                            const id = parseInt(e.target.value);
                                                            if (id && !formData.locationIds.includes(id)) {
                                                                setFormData({ ...formData, locationIds: [...formData.locationIds, id] });
                                                            }
                                                        }}
                                                    >
                                                        <option value="">+ Ajouter un emplacement...</option>
                                                        {locations.filter(l => !formData.locationIds.includes(l.id)).map(loc => (
                                                            <option key={loc.id} value={loc.id}>{loc.fullPath}</option>
                                                        ))}
                                                    </select>
                                                    <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px] pointer-events-none">expand_more</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </form>
                            </div>

                            {/* Sticky Footer with actions */}
                            <div className="shrink-0 px-8 py-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-800/50 flex items-center justify-between gap-4">
                                <p className="text-xs text-slate-400 font-medium">
                                    {editingProduct ? 'Les modifications seront sauvegardées.' : 'Le produit sera ajouté à l\'inventaire.'}
                                </p>
                                <button
                                    type="submit"
                                    form="product-form"
                                    className="flex items-center gap-2 px-8 py-3 bg-primary text-white rounded-xl font-black hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] text-sm"
                                >
                                    <span className="material-symbols-outlined text-[20px]">check_circle</span>
                                    {editingProduct ? 'Mettre à jour' : 'Créer le produit'}
                                </button>
                            </div>

                        </div>
                    </div>
                )}

                {/* Bulk Actions Menu */}
                {
                    selectedIds.length > 0 && (
                        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[80] animate-in fade-in slide-in-from-bottom-8 duration-300">
                            <div className="bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-6 border border-slate-800 dark:border-slate-200">
                                <div className="flex items-center gap-3 pr-6 border-r border-slate-700 dark:border-slate-200">
                                    <span className="bg-primary text-white size-6 rounded-full flex items-center justify-center text-xs font-bold">{selectedIds.length}</span>
                                    <span className="font-bold whitespace-nowrap">Sélectionnés</span>
                                </div>

                                <div className="flex items-center gap-4 relative">
                                    {/* Entrepot */}
                                    <div className="relative group/bulk">
                                        <button className="h-9 px-4 rounded-lg bg-slate-800 dark:bg-slate-100 border border-slate-700 dark:border-slate-300 text-sm font-bold flex items-center gap-2 hover:bg-slate-700 dark:hover:bg-slate-200 transition-colors">
                                            <span className="material-symbols-outlined text-[18px]">warehouse</span>
                                            Entrepôt
                                            <span className="material-symbols-outlined text-[18px]">keyboard_arrow_up</span>
                                        </button>
                                        <div className="absolute bottom-full left-0 mb-2 w-56 bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 py-2 opacity-0 invisible group-hover/bulk:opacity-100 group-hover/bulk:visible transition-all translate-y-2 group-hover/bulk:translate-y-0 z-10">
                                            <div className="px-3 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Assigner à l'entrepôt</div>
                                            {warehouses.map(w => (
                                                <button key={w.id} onClick={() => handleBulkUpdate({ warehouseId: w.id })} className="w-full px-4 py-2.5 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-3 transition-colors">
                                                    {w.name}
                                                </button>
                                            ))}
                                            <div className="my-1 border-t border-slate-100 dark:border-slate-800"></div>
                                            <button onClick={() => handleBulkUpdate({ warehouseId: null })} className="w-full px-4 py-2.5 text-left text-sm text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 flex items-center gap-3 transition-colors">
                                                Désassigner
                                            </button>
                                        </div>
                                    </div>

                                    {/* Catégorie */}
                                    <div className="relative group/cat">
                                        <button className="h-9 px-4 rounded-lg bg-slate-800 dark:bg-slate-100 border border-slate-700 dark:border-slate-300 text-sm font-bold flex items-center gap-2 hover:bg-slate-700 dark:hover:bg-slate-200 transition-colors">
                                            <span className="material-symbols-outlined text-[18px]">category</span>
                                            Catégorie
                                            <span className="material-symbols-outlined text-[18px]">keyboard_arrow_up</span>
                                        </button>
                                        <div className="absolute bottom-full left-0 mb-2 w-56 bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 py-2 opacity-0 invisible group-hover/cat:opacity-100 group-hover/cat:visible transition-all translate-y-2 group-hover/cat:translate-y-0 z-10">
                                            <div className="px-3 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Assigner à la catégorie</div>
                                            {categories.map(c => (
                                                <button key={c.id} onClick={() => handleBulkUpdate({ categoryId: c.id })} className="w-full px-4 py-2.5 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-3 transition-colors">
                                                    {c.name}
                                                </button>
                                            ))}
                                            <div className="my-1 border-t border-slate-100 dark:border-slate-800"></div>
                                            <button onClick={() => handleBulkUpdate({ categoryId: null })} className="w-full px-4 py-2.5 text-left text-sm text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 flex items-center gap-3 transition-colors">
                                                Désassigner
                                            </button>
                                        </div>
                                    </div>

                                    {/* Fournisseur */}
                                    <div className="relative group/sup">
                                        <button className="h-9 px-4 rounded-lg bg-slate-800 dark:bg-slate-100 border border-slate-700 dark:border-slate-300 text-sm font-bold flex items-center gap-2 hover:bg-slate-700 dark:hover:bg-slate-200 transition-colors">
                                            <span className="material-symbols-outlined text-[18px]">local_shipping</span>
                                            Fournisseur
                                            <span className="material-symbols-outlined text-[18px]">keyboard_arrow_up</span>
                                        </button>
                                        <div className="absolute bottom-full left-0 mb-2 w-56 bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 py-2 opacity-0 invisible group-hover/sup:opacity-100 group-hover/sup:visible transition-all translate-y-2 group-hover/sup:translate-y-0 z-10">
                                            <div className="px-3 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Ajouter au catalogue de</div>
                                            {Array.isArray(suppliers) && suppliers.map(s => (
                                                <button key={s.id} onClick={() => handleBulkUpdate({ supplierId: s.id })} className="w-full px-4 py-2.5 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-3 transition-colors">
                                                    {s.name}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="h-6 w-px bg-slate-700 dark:bg-slate-200 mx-2"></div>

                                    {/* Suppression */}
                                    <button
                                        onClick={handleBulkDelete}
                                        className="h-9 px-4 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold flex items-center gap-2 transition-colors shadow-lg shadow-rose-900/20"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">delete</span>
                                        Supprimer
                                    </button>
                                </div>

                                <button
                                    onClick={() => setSelectedIds([])}
                                    className="p-2 text-slate-400 hover:text-white dark:hover:text-slate-900 transition-colors"
                                >
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                        </div>
                    )
                }

                {/* Bulk Import Modal */}
                <GenericImportModal
                    isOpen={showImportModal}
                    onClose={() => setShowImportModal(false)}
                    fields={importFields}
                    onImport={handleImport}
                    title="Importer des Produits"
                />
            </div>
        </div>
    );
};

export default Inventory;
